import { LitElement, css, html } from "lit"
import { unsafeHTML } from "lit/directives/unsafe-html.js"
import { when } from "lit/directives/when.js"
const { apiFetch } = window.wp

export class VariationForm extends LitElement {
	static properties = {
		stock: { type: Number },
		variation_id: { type: Number },
		product_id: { type: Number },
		quantity: { type: Number },
		loading: { type: Boolean },
		message: { type: String },
		error: { type: Boolean },
		nonce: { type: String },
	}

	static styles = css`
		:host {
			display: block;
		}

		.variation-form {
			display: flex;
			align-items: center;
			gap: 28px;
		}

		.quantity-controls {
			display: flex;
			align-items: center;
			border-radius: 30px;
			border: 1px solid #1c1e1d;
		}

		.quantity-controls button {
			width: 30px;
			height: 30px;
			font-size: 20px;
			text-align: center;
			line-height: 1;
			background-color: transparent;
			border: none;
			cursor: pointer;
		}

		.quantity-controls span {
			min-width: 30px;
			padding: 0 10px;
			text-align: center;
		}

		.add-to-cart {
			color: var(--Typography-Headings, #1c1e1d);
			font-family: var(--Font-Family-Body, Hind);
			font-size: 12px;
			font-style: normal;
			font-weight: 600;
			line-height: 140%; /* 16.8px */
			letter-spacing: -0.36px;
			text-decoration-line: underline;
			text-decoration-style: solid;
			text-decoration-skip-ink: none;
			text-decoration-thickness: 12.5%; /* 1.5px */
			text-underline-offset: 24%; /* 2.88px */
			text-underline-position: from-font;
			outline: none;
			padding: 0;
			background-color: transparent;
			border: none;
			cursor: pointer;
		}

		button:disabled {
			cursor: not-allowed;
		}

		.message {
			margin-top: 10px;
			font-size: 14px;
		}

		.message.success {
			color: green;
		}

		.message.error {
			color: red;
		}

		input[type="number"] {
			width: 60px;
			padding: 5px;
			font-size: 16px;
			margin-right: 10px;
		}

		@media screen and (max-width: 600px) {
			.variation-form {
				gap: 1rem;
			}
			.quantity-controls {
				border: none;
				padding: 0;
			}

			.quantity-controls button {
				border: none;
				padding: 0;
			}

			.quantity-controls span {
				min-width: 15px;
				padding: 0;
			}
		}
	`

	constructor() {
		super()
		this.stock = 0
		this.variation_id = 0
		this.product_id = 0
		this.quantity = 1
		this.loading = false
		this.message = ""
		this.error = false
		this.nonce = ""
		this.getNonce()
	}

	async getNonce() {
		//header value in wp-json/wc/store/v1/cart
		let response = await apiFetch({ path: "/wc/store/v1/cart", parse: false })
		this.nonce = response.headers.get("Nonce")
	}

	render() {
		return html`
			<div class="variation-form">
				<div class="quantity-controls">
					<button
						class="decrease"
						@click="${() => {
							if (this.quantity > 1) this.quantity--
						}}"
						?disabled="${this.quantity <= 1}">
						–
					</button>
					<span>${this.quantity}</span>
					<button
						class="increase"
						@click="${() => {
							if (this.quantity < this.stock) this.quantity++
						}}"
						?disabled="${this.quantity >= this.stock}">
						+
					</button>
				</div>
				<button
					class="add-to-cart"
					@click="${this.addToCart}"
					?disabled="${this.loading ||
					this.stock === 0 ||
					this.quantity < 1 ||
					this.quantity > this.stock ||
					!this.nonce}">
					${when(
						this.loading,
						() => html`<span>Laddar...</span>`,
						() => html`<span>Lägg till</span>`
					)}
				</button>
			</div>
		`
	}

	onQuantityChange(e) {
		const value = parseInt(e.target.value, 10)
		if (value > this.stock) {
			this.quantity = this.stock
		} else if (value < 1) {
			this.quantity = 1
		} else {
			this.quantity = value
		}
	}

	async addToCart() {
		this.loading = true
		this.message = ""
		this.error = false

		try {
			const response = await apiFetch({
				path: "/wc/store/v1/cart/add-item",
				method: "POST",
				data: {
					id: this.variation_id,
					quantity: this.quantity,
				},
				headers: {
					Nonce: this.nonce,
				},
				parse: false,
			})

			if (response && response.status === 201) {
				this.message = 'Product added to cart. <a href="/cart">View Cart</a>'
				this.stock -= this.quantity
				this.quantity = 1
				this.error = false

				let event = new CustomEvent("cart-updated", {
					detail: { item: { id: this.variation_id, quantity: this.quantity } },
					bubbles: true,
					composed: true,
				})
				this.dispatchEvent(event)
				document.dispatchEvent(event)
				if (window.WC && WC.CartFragments) {
					WC.CartFragments.refresh()
				}

				// Google Analytics gtag event
				if (window.gtag) {
					gtag("event", "add_to_cart", {
						currency: "SEK",
						items: [
							{
								item_id: this.variation_id,
								item_name: "", // You can add product name here if available
								quantity: this.quantity,
							},
						],
					})
				}
			} else {
				this.message = "Failed to add product to cart."
				this.error = true
			}
		} catch (error) {
			this.message = "An error occurred. Please try again."
			this.error = true
		} finally {
			this.loading = false
		}
	}
}
